// Super Admin (Platform / Master Admin) manual. Operates the whole platform
// across every tenant; has no tenant of their own and passes all checks.

const manual = {
  key: 'super_admin',
  role: 'Super Admin',
  title: 'User Manual — Super Admin (Platform)',
  subtitle:
    'For the platform operator: run the SaaS across every company — tenants, plans, billing, usage, logs and platform settings.',
  sections: [
    {
      id: 'welcome',
      title: 'Welcome',
      blocks: [
        'As Super Admin (Master Admin) you run the platform itself, not one company. You create and manage tenant companies, set the plans they buy, oversee billing and coupons, watch platform-wide usage and logs, manage system email templates, triage support, and control platform settings like maintenance mode and sign-in options. You sit outside every tenant and pass all permission checks.',
        'Your panel lives under /super-admin with its own sidebar. You do no day-to-day task work here — that belongs to the companies you host.',
      ],
    },
    {
      id: 'page-guide',
      title: 'Page-by-Page Guide',
      blocks: [
        'This guide walks your sidebar top to bottom: each page’s purpose, its buttons and actions, and its form fields. Add/Edit forms open in a dialog; row actions are icons at the end of each row.',

        { sub: 'Dashboard (/super-admin/dashboard)' },
        { image: '/manual/super-dashboard.png', caption: 'Dashboard — platform-wide health: KPI tiles, growth charts, Action Needed, Workload Health and System Health.' },
        'The platform dashboard summarises the health of the whole service.',
        {
          list: [
            'KPI strip — Active Companies, Total Companies, Active Users and Total Users, each linking to its list.',
            'Growth — Company Signups (15/30/45-day column chart) and Plan Distribution by tier.',
            'Billing — revenue tiles (currently placeholders).',
            'Action Needed — the Support Tickets inbox (open / urgent / overdue) and Platform Risks by severity (storage, AI, payment and trial-ending).',
            'Workload Health — Storage Usage by Company and AI Usage by Company (top tenants against their caps).',
            'Recent Platform Events — a platform-wide audit feed, linking to the full log.',
            'System Health — API, Database, Redis and Email Queue status with last-checked times.',
          ],
        },

        { sub: 'Companies (/super-admin/companies)' },
        { image: '/manual/super-companies.png', caption: 'Companies — every tenant, with create/edit, plan and status controls.' },
        'Every tenant company, with plan, status and usage at a glance.',
        {
          list: [
            'Create Company — opens the new-company dialog.',
            'Row actions: Edit (Organisation Name, Plan, Country); Manage plan (change plan, with a downgrade confirmation); Resend invite (pending only); Suspend / Reactivate; Offboard; Delete. Suspend and Offboard each take an optional reason (≤500 chars).',
            'Filters: search by name, plus Plan, Status and Country. A KPI strip shows Total / Active / Inactive / Trial.',
            'Open a company for its read-only detail page (identity, owner, KPI strip, settings snapshot). Make changes from the list, not the detail page.',
          ],
        },

        { sub: 'Analytics (/super-admin/analytics)' },
        'A cross-company progress dashboard (Super Admin only): KPI cards, a Task Status donut, and company-wise + employee-wise tables spanning every tenant. Drill into a single company to see just its numbers.',
        {
          list: [
            'Filters: Company (drill-down to one tenant), Department (within a single company) and a Period selector (All time / Today / This week / This month / custom range). "Today" scopes to tasks created or running today.',
            'KPI cards: Companies, Employees, Tasks, Complete, To Do, In Progress, Overdue, Goals completed, Reports submitted and Verification pending.',
            'Task Status donut splits every task into To Do, In Progress, Complete and Overdue, coloured from the Space workflow.',
            'Company table (Employees, Tasks, Complete, Open, Overdue, Completion %, Goals, Reports) and an employee table (per-person Assigned / Completed / Open / Overdue / Goals / Reports), each with search, sortable columns and pagination.',
            'Export — download either table (CSV, Excel and more) honouring the active filters; names and email are included.',
          ],
        },

        { sub: 'Billing › Plans (/super-admin/plans)' },
        { image: '/manual/super-plans.png', caption: 'Billing › Plans — the plan catalogue: pricing, limits and feature toggles.' },
        'The subscription plans you sell.',
        {
          list: [
            'Create Plan — opens the editor; per-row Edit / Delete.',
            'Filter chips: All / Trial / Paid / Default / Inactive, plus search.',
            'Plan form: Name and Code; toggles for Active, Set as default, Popular and Public; a Trial/Paid type (trial days, or monthly/yearly price + currency); resource caps (Max Users, Storage GB, AI Tokens/month — blank means unlimited); and a feature checklist (Workflows, Gamification, External Collaboration, …).',
          ],
        },

        { sub: 'Billing › Payments (/super-admin/payments)' },
        { image: '/manual/super-payments.png', caption: 'Billing › Payments — the platform-wide transaction feed.' },
        'Revenue. Tabs for Transactions and Invoices, with KPI tiles (monthly / total revenue, pending, failed). Filter by date range, company and status. Preview a transaction, or Download PDF on an invoice.',

        { sub: 'Billing › Coupons (/super-admin/coupons)' },
        { image: '/manual/super-coupons.png', caption: 'Billing › Coupons — create and manage discount coupons.' },
        'Promotional discount codes. Create Coupon opens the editor; filter by status (Active / Expired / Exhausted / Scheduled / Off). The form takes a Code, optional description, an Active toggle, a percentage or flat discount (currency for flat), valid-from / valid-until dates and an optional max-redemptions cap.',

        { sub: 'Logs › Visitor Logs (/super-admin/visitor-logs)' },
        { image: '/manual/super-visitor-logs.png', caption: 'Logs › Visitor Logs — platform-wide page-view and session tracking.' },
        'Platform-wide page-view and session analytics (Super Admin only). A window selector (24 hours / 7 / 30 days), CSV / XLSX / PDF export, KPI tiles, and tables for Page Visits, Sessions and Page Analytics, plus a retention-settings dialog. (The Logs group also holds Audit Logs, Login Activity and the Email Status Logs below.)',

        { sub: 'Logs › Email Status Logs (/super-admin/email-logs)' },
        { image: '/manual/super-email-logs.png', caption: 'Logs › Email Status Logs — every email the system sent, with delivery status.' },
        'Every email sent, with delivery status. KPI tiles (Sent / Delivered / Bounced / Failed), filters (date range, template, status, recipient) and a per-row preview of the rendered email. Retry failed rows.',

        { sub: 'Email Templates (/super-admin/email-templates)' },
        { image: '/manual/super-email-templates.png', caption: 'Email Templates — edit the system emails and assign them to events.' },
        'Every managed email the platform sends. Two tabs — Templates and Assignments.',
        {
          list: [
            'Templates: Create Template, plus per-row Edit / Preview / Delete and an on/off status toggle. The editor is a full page: Template Name, Template Key, an Active toggle, Subject and Body HTML (both support {{variable}} tokens), a list of available variables and a live preview.',
            'Assignments: maps each event to a template, with an on/off toggle per event.',
          ],
        },

        { sub: 'Leads (/super-admin/leads)' },
        { image: '/manual/super-leads.png', caption: 'Leads — inbound leads captured from Meta / Google / website / webhook sources.' },
        'Inbound enquiries and integration leads. The Integrations button opens the ingestion settings (endpoint URLs + token for Meta / Google / Webhook / N8N). Filter by Source and search. Open a row (eye icon) to read the full enquiry, or delete a spam/test row.',

        { sub: 'Support tickets (/super-admin/support-tickets)' },
        { image: '/manual/super-support-tickets.png', caption: 'Support tickets — triage tickets raised by companies.' },
        'The cross-company support queue. Filter by status, category, priority and date. Open a ticket to read the thread, reply, and change its status, priority or assignee.',

        { sub: 'Announcements (/super-admin/announcements)' },
        { image: '/manual/super-announcements.png', caption: 'Announcements — broadcast a message to every company.' },
        'Broadcast to every company. The compose form takes a Title (3–200 chars), Message (3–5000 chars), an Audience (All members / Company Admins) and a Priority. Publish sends it to every matching bell. A table lists past announcements with a Delete action.',

        { sub: 'Settings (/super-admin/settings)' },
        { image: '/manual/super-settings.png', caption: 'Settings — platform-wide controls: maintenance mode, sign-in options, branding and more.' },
        'Platform-wide configuration, organised as tabs:',
        {
          table: {
            head: ['Tab', 'What you set'],
            rows: [
              ['General', 'Platform name, support email and other platform defaults (each with a Reset-to-default, plus a single Save Changes).'],
              ['Branding', 'Upload the platform logo and website logo (PNG/JPEG/WebP/SVG, ≤2 MB).'],
              ['Email', 'The email connectors — set each provider’s credentials + sender addresses, test them, and mark one active (see the Email connectors work above).'],
              ['Google SSO', 'Client ID + secret and an Enabled toggle for Google sign-in.'],
              ['Session', 'Session / inactivity timeout settings.'],
              ['Maintenance', 'Turn on maintenance mode and set the message users see (Super Admin is always exempt).'],
              ['IP Access', 'IP allow-list / block-list entries.'],
              ['AI Firewall', 'Turn on AI content screening and manage its rules.'],
            ],
          },
        },
      ],
    },
    {
      id: 'daily-workflow',
      title: 'Daily Workflow',
      blocks: [
        {
          steps: [
            'Log in to the Dashboard and check System Health and Platform Risks.',
            'Work the Support Tickets inbox — clear urgent and overdue first.',
            'Review new Companies and Leads; onboard or follow up as needed.',
            'Scan Usage (AI and Storage) for tenants nearing their caps.',
            'Check Logs to investigate anomalies, and use Announcements for anything the whole platform must know.',
          ],
        },
      ],
    },
    {
      id: 'task-management',
      title: 'Task Management (not applicable)',
      blocks: [
        'Task, Space, List and SOP work happens inside each tenant company, not in the platform panel. As Super Admin you manage the companies that do that work; you create no tasks or reports here.',
        { note: 'To see a company’s internal data, use the Companies page and the platform Usage/Logs surfaces rather than acting inside the tenant.' },
      ],
    },
    {
      id: 'reports',
      title: 'Reports',
      blocks: [
        'Reporting here is platform usage tracking, not personal Work Reports. Usage Report and the Usage group give cross-tenant analytics — active users, AI token spend and storage. The Growth charts on the dashboard track signups and plan distribution. Data Platforms exposes a controlled, read-only connection for external analytics tools.',
      ],
    },
    {
      id: 'notifications',
      title: 'Notifications',
      blocks: [
        'Platform-level notifications (for example support-ticket activity) arrive at the header bell. Announcements you send reach every company’s users. There is no notifications row in the sidebar — use the bell’s "View all notifications".',
      ],
    },
    {
      id: 'faq',
      title: 'Frequently Asked Questions',
      blocks: [
        { sub: 'Where do I turn on maintenance mode or change sign-in options?' },
        'On the Settings page — Maintenance, IP Access and Google SSO are sections there. Super Admin is always exempt from maintenance mode.',
        { sub: 'How do I change a company’s plan?' },
        'Open the company from the Companies list and edit its plan.',
        { sub: 'How do I message every company at once?' },
        'Use Announcements — it broadcasts to every company’s notification bell.',
        { sub: 'Can I edit the emails the system sends?' },
        'Yes, on Email Templates. Code-based templates and these managed templates are separate systems; editing one does not change the other.',
      ],
    },
    {
      id: 'best-practices',
      title: 'Best Practices',
      blocks: [
        {
          list: [
            'Start each day with System Health and Platform Risks — catch outages and at-cap tenants early.',
            'Keep support SLAs by clearing urgent and overdue tickets first.',
            'Review Plans and Coupons changes carefully — they affect every customer who buys them.',
            'Prefer the platform surfaces (Companies, Usage, Logs) over reaching inside a tenant.',
            'Use Announcements sparingly and clearly — it reaches everyone.',
          ],
        },
      ],
    },
  ],
}

export default manual
